/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.14.0
 */

import { useState } from 'react';

import { useFormikContext } from 'formik';

import { Input } from '@components/common';
import type { IInputSearchProps } from '@components/common/InputSearch/InputSearch';
import { InputSearch } from '@components/common/InputSearch/InputSearch';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';

import type { IFormFieldProps } from './FormField';
import FormField from './FormField';

interface IFormFieldFieldInputSearch extends IFormFieldProps {
  icon?: string;
  max?: number;
  min?: number;
  onSelect?: (value: IInputSearchItem) => void;
  placeholder?: string;
  regex?: RegExp;
}

type TFormFieldInputSearchProps = IFormFieldFieldInputSearch &
  Omit<IInputSearchProps, 'children' | 'onSelect'>;

const FormFieldInputSearch = ({
  foundItems,
  icon,
  placeholder,
  onChange,
  onSelect,
  virtual,
  ...props
}: TFormFieldInputSearchProps) => {
  const [onChangeAddressTimeout, setChangeAddressTimeout] = useState(0);

  const { setFieldValue, setFieldTouched } = useFormikContext();

  return (
    <FormField {...props} errorKey={`${props.name}.value`}>
      {({ field, kind, disabled, label, dataName, required }) => {
        const onChangeHandler = (value: string) => {
          const newValue = value.trimStart();

          if (props.max && newValue && newValue.length > props.max) {
            return;
          }

          if (props.regex && newValue && !newValue.match(props.regex)) {
            return;
          }

          setFieldValue(field.name, { value: newValue, id: '' }, true);
          if (onChangeAddressTimeout > 0) {
            clearTimeout(onChangeAddressTimeout);
          }
          setChangeAddressTimeout(
            window.setTimeout(() => {
              onChange(newValue);
              setFieldTouched(field.name, true, true);
            }, DEBOUNCE_DEFAULT_DELAY),
          );
        };

        const onSelectHandler = (value: IInputSearchItem) => {
          setFieldValue(field.name, value);
          onSelect?.(value);
        };

        const onFocus = () => {
          if (!field.value?.value && !foundItems?.length) {
            setFieldValue(field.name, { value: '', id: '' });
            setTimeout(() => {
              onChange('');
              setFieldTouched(field.name, true, true);
            }, DEBOUNCE_DEFAULT_DELAY);
          }
        };

        return (
          <InputSearch
            foundItems={foundItems}
            virtual={virtual}
            onChange={onChangeHandler}
            onSelect={onSelectHandler}
          >
            <Input
              dataName={dataName}
              disabled={disabled}
              kind={kind}
              label={label}
              max={props.max}
              min={props.min}
              placeholder={placeholder}
              required={required}
              righticon={icon}
              value={field.value?.value}
              onFocus={onFocus}
            />
          </InputSearch>
        );
      }}
    </FormField>
  );
};

export default FormFieldInputSearch;
